import React from 'react'

import { MenuButton } from '@repo/ui'

export type SortMenuDesktopField = { label: string; value: string }

export type SortMenuDesktopProps = {
  anchorEl: HTMLElement | null
  open: boolean
  onClose: () => void
  /**
   * A field was picked. **The caller must hand off to `SortConfigPopover` here** —
   * see "The pick HANDS OFF" in this component's doc below. `anchor` is this
   * menu's own `anchorEl` (the Sort button), passed back so the config popover can
   * be opened on the same element without the call site re-reading its own anchor
   * state — which is a stale-closure trap when the pick is what clears it.
   */
  onPickField: (field: string, anchor: HTMLElement | null) => void
  /**
   * The sortable fields, when they are not the certificates list's.
   *
   * Optional and defaulted, so this is the certificates list's own menu unless a
   * caller says otherwise — the list is unchanged by this prop existing. It is
   * here because the certificate SUB-VIEWS sort on different columns (Activity:
   * Date / Event type) and needed the same PRESENTATION, not the same data. The
   * alternative was a second near-identical wrapper around `MenuButton`, which is
   * exactly the drift working-conventions §36 names.
   */
  fields?: SortMenuDesktopField[]
}

const FIELDS: SortMenuDesktopField[] = [
  { label: 'Certificate name', value: 'name' },
  { label: 'Status', value: 'status' },
  { label: 'Last activity date', value: 'last_activity' },
]

/**
 * The desktop sort-field picker — now literally platform's, not a lookalike.
 *
 * `@repo/ui`'s `MenuButton` is the component `apps/platform`'s toolbar `Sort`
 * renders for exactly this list (`Sort.tsx` passes it `items={sortColumns}`),
 * so this file renders THE SAME component rather than a second implementation
 * of it. Nothing about the rows is described here, which is the point: row
 * height, padding, radius, hover and font all live in `MenuButton` and there is
 * no local value left to drift from platform's.
 *
 * What this replaced was a hand-rolled `MuiMenu`/`MenuItem` list annotated
 * "Platform parity (ActionDropdown/Select)" — parity asserted in a comment,
 * which working-conventions §31 and §36 both name as the failure mode. It had
 * in fact copied platform's spacing FACTORS (`py: 3`, `px: 2`), and factors do
 * not survive the trip: `@repo/ui` sets a 4px spacing base while `apps/app`
 * keeps MUI's 8px default (§29), so `py: 3` was 12px in platform and rendered
 * 24px here. Rows measured 72px against platform's 48 — the "way too tall" this
 * fixes, and a defect a shared comment could never have caught.
 *
 * Two changes to `MenuButton` were needed to make it consumable here, both
 * additive and both pixel-neutral for platform (see that file):
 *   1. a CONTROLLED mode — the Sort trigger lives in `HeaderBar` with its own
 *      count badge and clear-X, so the menu has to take an external `anchorEl`
 *      instead of wrapping its own button; and
 *   2. its menu geometry moved from `theme.spacing`/`shape.borderRadius`
 *      multipliers to absolute px, which is §47's standing requirement for a
 *      layer-1 component and the only way one can render the same in both apps.
 *
 * ## The pick HANDS OFF to the config popover — it does not just close
 *
 * Picking a field opens `SortConfigPopover` on this same anchor. It used to apply
 * the sort and close everything, which is why this is spelled out as a contract on
 * `onPickField` rather than left to each call site: four screens wire this menu,
 * and all four had independently written the closing version.
 *
 * That is platform's behaviour, literally — `apps/platform`'s `Sort.tsx`
 * `handleAddSort` opens its own menu when it is the FIRST sort
 * (`if (sort.length == 0) setAnchorEl(sortButtonRef.current)`), so one click on a
 * field leaves you looking at that sort as a row you can then re-aim, reverse,
 * remove, or add a second to. And it is what this app's own PHONE half already
 * did: `SortDrawer`'s pick step calls `setStep('configure')`. The desktop menu was
 * the only one of the three that dead-ended.
 *
 * Why it matters beyond consistency: sorting is multi-field here (up to three).
 * A picker that closes on the first pick presents a multi-sort control as a
 * single-choice menu — the second sort exists but nothing on screen says so, and
 * you have to re-open the same button to discover the surface changed underneath.
 *
 * Hover needs no override here. `MenuButton` uses `navHover()`, which is
 * `palette.action.hover` — a NEUTRAL black-4%, not a brand tint, so it does not
 * degrade the way `alpha(brand ?? primary.main, 0.12)` did in this app (where
 * `primary.main` IS black and that expression collapsed to a flat 12% black).
 * It computes to ~#f5f5f5 here, matching the `grey[50]` the mobile half of this
 * same list uses in `SortDrawer`.
 */
export const SortMenuDesktop: React.FC<SortMenuDesktopProps> = ({
  anchorEl,
  open,
  onClose,
  onPickField,
  fields = FIELDS,
}) => (
  <MenuButton
    open={open}
    anchorEl={anchorEl}
    onClose={onClose}
    items={fields}
    onSelect={(item: { value: string }) => onPickField(item.value, anchorEl)}
    // Wide enough for "Last activity date" on one line; matches the width the
    // popover already had, so only its height changes.
    menuMinWidth={220}
    /* 12, matching this app's other desktop dropdown (`components/UI/ActionMenu`),
       NOT the shared default of 8, which is platform's.

       Caught at the origin/main merge: main had set this menu's paper to 12 in a
       chrome sweep, and adopting MenuButton reverted it to 8 without anything
       flagging the change. Two dropdowns in the same toolbar row with different
       corners is the drift this file exists to remove, so the radius is now a
       MenuButton prop rather than a reason to fork it again (§47). */
    menuRadius={12}
    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
  />
)

export default SortMenuDesktop
